/**
 * Web Audio Manager (Core)
 * 
 * Main orchestrator for audio services.
 * Coordinates recording, playback, and voice activity detection.
 * 
 * This is the main public API for audio functionality.
 */

import { AUDIO_CONFIG } from '../../config/realtime';
import { AudioConfig, AudioLevel } from './types';
import { AudioPlayback, PlaybackEvents } from './AudioPlayback';
import { AudioRecording, RecordingEvents } from './AudioRecording';
import { VADState } from './VoiceActivityDetection';
import { logger } from '../../utils/logger';

export type { AudioConfig, AudioLevel, VADState };

export interface WebAudioManagerEvents extends PlaybackEvents, RecordingEvents {
  // Combined events from all modules
}

class WebAudioManager {
  private audioContext: AudioContext | null = null;
  private config: AudioConfig;
  private events: Partial<WebAudioManagerEvents> = {};
  
  // Module instances
  private playback: AudioPlayback | null = null;
  private recording: AudioRecording | null = null;
  private microphoneReady = false;

  // Pending timer to honour the playback-end delay (see setPlaybackActiveDelayed).
  // Cancelled if a new chunk arrives before it fires.
  private pendingPlaybackInactiveTimer: ReturnType<typeof setTimeout> | null = null;

  // Per-scenario VAD flag. Stored here so callers can set it before initializeAudio()
  // creates the recording module; applied as soon as recording exists.
  private atypicalVoiceMode = false;

  // Default configuration optimized for speech
  private defaultConfig: AudioConfig = {
    sampleRate: AUDIO_CONFIG.sampleRate,
    channelCount: AUDIO_CONFIG.channelCount,
    echoCancellation: AUDIO_CONFIG.echoCancellation,
    noiseSuppression: AUDIO_CONFIG.noiseSuppression,
    autoGainControl: AUDIO_CONFIG.autoGainControl,
    volume: 1.0,
  };

  constructor(config: Partial<AudioConfig> = {}) {
    this.config = { ...this.defaultConfig, ...config };
  }

  /**
   * Register event listeners
   */
  on<K extends keyof WebAudioManagerEvents>(event: K, callback: WebAudioManagerEvents[K]): void {
    this.events[event] = callback;
    
    // Forward to modules
    if (this.playback && (event === 'onPlaybackStart' || event === 'onPlaybackStop')) {
      this.playback.on(event as keyof PlaybackEvents, callback as any);
    }
    if (this.recording && (event === 'onRecordingStart' || event === 'onRecordingStop' || event === 'onAudioLevel')) {
      this.recording.on(event as keyof RecordingEvents, callback as any);
    }
  }

  /**
   * Remove event listeners
   */
  off<K extends keyof WebAudioManagerEvents>(event: K): void {
    delete this.events[event];
    
    // Forward to modules
    if (this.playback) {
      this.playback.off(event as keyof PlaybackEvents);
    }
    if (this.recording) {
      this.recording.off(event as keyof RecordingEvents);
    }
  }

  /**
   * Configure audioSession for mobile devices (Safari iOS only)
   * Chrome mobile doesn't support this API
   */
  private configureAudioSession(): void {
    try {
      if ((navigator as any).audioSession) {
        (navigator as any).audioSession.type = 'play-and-record';
        logger.debug('[🎙️ AEC] ✅ audioSession set to "play-and-record"');
      }
    } catch (e) {
      console.warn('[🎙️ AEC] ⚠️ Failed to set audioSession:', e);
    }
  }

  /**
   * Initialize audio context and setup audio pipeline
   */
  async initializeAudio(): Promise<void> {
    try {
      // Firefox honors the sampleRate hint in the AudioContext constructor; Chrome
      // ignores it. OpenAI sends PCM16 at 24kHz, and our ScriptProcessor was
      // up-sampling 24→48 by linear interpolation inside the audio thread on every
      // callback. Forcing 24kHz on Firefox eliminates that resample entirely (1:1
      // sample passthrough). Skipped on Chrome because the hint is silently ignored
      // and we already know the buffered path works fine there.
      const ua = navigator.userAgent;
      const isFirefox = ua.includes('Firefox');
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (isFirefox) {
        try {
          this.audioContext = new Ctx({ latencyHint: 'interactive', sampleRate: 24000 });
          logger.info('[🎙️ AEC] Firefox: AudioContext opened at 24kHz (resample-free path)');
        } catch (e) {
          logger.info('[🎙️ AEC] Firefox: 24kHz AudioContext rejected, falling back to default rate', e);
          this.audioContext = new Ctx({ latencyHint: 'interactive' });
        }
      } else {
        this.audioContext = new Ctx({ latencyHint: 'interactive' });
      }

      // Configure audioSession for system-level AEC (first time)
      this.configureAudioSession();

      // Resume audio context if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Configure audioSession again after resume (may be needed on some devices)
      this.configureAudioSession();

      // Update config to match actual sample rate
      this.config.sampleRate = this.audioContext.sampleRate;

      logger.info('[🎙️ AEC] ✅ AudioContext initialized:', { 
        sampleRate: this.audioContext.sampleRate,
        state: this.audioContext.state
      });
      
      // Warm-up: Play silent audio to initialize AEC engine
      try {
        const silentBuffer = this.audioContext.createBuffer(1, this.audioContext.sampleRate * 0.1, this.audioContext.sampleRate);
        const warmupSource = this.audioContext.createBufferSource();
        warmupSource.buffer = silentBuffer;
        warmupSource.connect(this.audioContext.destination);
        warmupSource.start();
        logger.debug('[🎙️ AEC] ✅ AEC warm-up complete');
        
        // Configure audioSession after warm-up (some devices need this)
        setTimeout(() => this.configureAudioSession(), 100);
      } catch (e) {
        console.warn('[🎙️ AEC] ⚠️ Warm-up failed:', e);
      }

      // Create module instances
      // Note: VAD is created internally by AudioRecording during setupRecording(),
      // since it needs the mic source/gain nodes. All VAD access goes through AudioRecording.
      this.playback = new AudioPlayback(this.audioContext, this.config);
      this.recording = new AudioRecording(this.audioContext, this.config);
      this.recording.setAtypicalVoiceMode(this.atypicalVoiceMode);

      // Forward all events from modules
      this.playback.on('onError', (error) => this.events.onError?.(error));
      this.playback.on('onPlaybackStart', () => {
        // Reconfigure audioSession when playback starts
        this.configureAudioSession();
        this.events.onPlaybackStart?.();
      });
      this.playback.on('onPlaybackStop', () => this.events.onPlaybackStop?.());
      
      this.recording.on('onError', (error) => this.events.onError?.(error));
      this.recording.on('onRecordingStart', () => {
        // Reconfigure audioSession when recording starts
        this.configureAudioSession();
        this.events.onRecordingStart?.();
      });
      this.recording.on('onRecordingStop', () => this.events.onRecordingStop?.());
      this.recording.on('onAudioLevel', (level) => this.events.onAudioLevel?.(level));
      this.recording.on('onAudioData', (data) => this.events.onAudioData?.(data));
      this.recording.on('onCommitAudio', () => this.events.onCommitAudio?.());
      this.recording.on('onSpeechDetected', () => this.events.onSpeechDetected?.());
      this.recording.on('onSpeechLikely', () => this.events.onSpeechLikely?.());
      this.recording.on('onPTTSpeechDetected', () => this.events.onPTTSpeechDetected?.());
      this.recording.on('onGateForceClosed', () => this.events.onGateForceClosed?.());
      this.recording.on('onFalsePositive', (reason) => this.events.onFalsePositive?.(reason));
      this.recording.on('onSpeechStoppedTimeout', () => this.events.onSpeechStoppedTimeout?.());

    } catch (error) {
      console.error('Error initializing audio context:', error);
      this.events.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Request microphone access and setup recording
   */
  async setupRecording(): Promise<void> {
    if (!this.recording) {
      throw new Error('Audio not initialized. Call initializeAudio() first.');
    }

    await this.recording.setupRecording();

    // Wire playback polling: VAD checks the buffer state every 20ms as a fallback.
    // Combined with the explicit setPlaybackActive(true) on each streaming chunk,
    // playbackActive stays true as long as EITHER condition holds.
    this.recording.setPlaybackChecker(() => this.playback?.isStreamingPlaybackActive() ?? false);
    
    // 🔧 FIX: AudioRecording.setupRecording() may have recreated the AudioContext
    // at the microphone's native sample rate (e.g. Bluetooth HFP at 16kHz instead
    // of the output device's 48kHz). Sync our reference so playback and other
    // modules use the same context.
    const recordingContext = this.recording.getAudioContext();
    if (recordingContext !== this.audioContext) {
      logger.info('[🎙️ AEC] AudioContext was recreated by recording setup — syncing references');
      this.audioContext = recordingContext;
      this.config.sampleRate = recordingContext.sampleRate;
      
      // Recreate playback module with the new context so it decodes/plays at the right rate
      if (this.playback) {
        this.playback.cleanup();
        this.playback = new AudioPlayback(this.audioContext, this.config);
        this.playback.on('onError', (error) => this.events.onError?.(error));
        this.playback.on('onPlaybackStart', () => {
          this.configureAudioSession();
          this.events.onPlaybackStart?.();
        });
        this.playback.on('onPlaybackStop', () => this.events.onPlaybackStop?.());
        logger.info('[🎙️ AEC] ✅ Playback module recreated with new AudioContext');
      }
    }
    
    this.microphoneReady = true;
    
    // Reconfigure audioSession after getUserMedia (Safari iOS only)
    this.configureAudioSession();
    
    // Initialize VAD if available
    // Note: VAD initialization would need source/gain nodes from recording
    // This is a simplified version - full integration pending
  }

  /**
   * Ensure microphone is initialized (idempotent)
   */
  async ensureMicrophoneReady(): Promise<void> {
    if (!this.recording) {
      throw new Error('Audio not initialized. Call initializeAudio() first.');
    }

    const existingStream = this.recording.getMediaStream();
    if (existingStream) {
      this.microphoneReady = true;
      return;
    }

    if (!this.microphoneReady) {
      await this.setupRecording();
    }
  }

  // ============================================================================
  // Recording Methods (delegated to AudioRecording)
  // ============================================================================

  startRecording(): void {
    this.recording?.startRecording();
  }

  stopRecording(): void {
    this.recording?.stopRecording();
  }

  pauseMicrophone(): void {
    this.recording?.pauseMicrophone();
  }

  resumeMicrophone(): void {
    this.recording?.resumeMicrophone();
  }

  isRecordingActive(): boolean {
    return this.recording?.isRecordingActive() ?? false;
  }

  resetVADState(): void {
    this.recording?.resetVADState();
  }
  
  prepareForNextSpeech(): void {
    this.recording?.prepareForNextSpeech();
  }
  
  markSpeechStarted(): void {
    this.recording?.markSpeechStarted();
  }

  // ============================================================================
  // PTT (Push-to-Talk) Methods
  // ============================================================================

  setPTTMode(enabled: boolean): void {
    this.recording?.setPTTMode(enabled);
  }

  isPTTModeActive(): boolean {
    return this.recording?.isPTTModeActive() ?? false;
  }

  isPTTTransmitting(): boolean {
    return this.recording?.isPTTTransmitting() ?? false;
  }

  startPTTTransmit(): void {
    this.recording?.startPTTTransmit();
  }

  stopPTTTransmit(): boolean {
    return this.recording?.stopPTTTransmit() ?? false;
  }

  getRecordingFrequencyData(): Uint8Array {
    return this.recording?.getRecordingFrequencyData() ?? new Uint8Array(9).fill(0);
  }

  getMediaStream(): MediaStream | null {
    return this.recording?.getMediaStream() ?? null;
  }

  // ============================================================================
  // Playback Methods (delegated to AudioPlayback)
  // ============================================================================

  playPCM16Data(pcm16Data: Uint8Array, id = 'default'): void {
    this.playback?.playPCM16Data(pcm16Data, id);
  }

  playPCM16DataStreaming(pcm16Data: Uint8Array): void {
    this.setPlaybackActiveImmediate(true);
    this.playback?.playPCM16DataStreaming(pcm16Data);
  }

  resetStreamingBuffer(): void {
    // Hard reset (interrupts, teardown) — drop the playback flag immediately,
    // because there's no audio left to come out of the speakers.
    this.setPlaybackActiveImmediate(false);
    this.playback?.resetStreamingBuffer();
  }

  /**
   * Soft reset for the next response. Keeps the master audio chain intact, so back-to-back
   * responses don't click on Firefox. Use this on the normal response→response transition;
   * `resetStreamingBuffer()` stays for interrupts and teardown.
   */
  softResetForNextResponse(): void {
    // Delay the false-edge by the audible playback tail so the VAD's playbackActive
    // guard stays on while the speaker is still draining the buffer.
    this.setPlaybackActiveDelayedFalse();
    this.playback?.softResetForNextResponse();
  }

  /**
   * Set playback flag immediately (no delay). Use for true-edges (chunk arrived,
   * VAD should start guarding now even though sound is still in the buffer) and
   * hard interrupts (teardown / cancel — there's nothing left to play).
   */
  private setPlaybackActiveImmediate(active: boolean): void {
    if (this.pendingPlaybackInactiveTimer) {
      clearTimeout(this.pendingPlaybackInactiveTimer);
      this.pendingPlaybackInactiveTimer = null;
    }
    this.recording?.setPlaybackActive(active);
  }

  /**
   * Schedule a setPlaybackActive(false) for after the audible tail finishes.
   *
   * The VAD's playbackActive flag was previously dropped the moment the JS
   * pipeline ran out of chunks — but on Firefox Mobile (and any ScriptProcessor
   * path) the speaker is still draining a buffer that's hundreds of ms ahead of
   * the JS side. During that window the VAD has no playbackActive guard and the
   * mic catches the avatar's tail, opening the gate. By delaying the false edge
   * until the speaker has actually gone silent we keep the guard on long enough.
   */
  private setPlaybackActiveDelayedFalse(): void {
    const tailMs = this.estimatePlaybackTailMs();
    if (this.pendingPlaybackInactiveTimer) {
      clearTimeout(this.pendingPlaybackInactiveTimer);
    }
    this.pendingPlaybackInactiveTimer = setTimeout(() => {
      this.pendingPlaybackInactiveTimer = null;
      this.recording?.setPlaybackActive(false);
    }, tailMs);
  }

  /**
   * Estimate how many ms of audio are still queued to come out of the speaker.
   * Uses bufferedSamples for the ScriptProcessor path (most accurate), falling
   * back to prebufferTime as a conservative ceiling. A safety margin handles
   * the browser's own output stage (HTMLAudioElement / OS mixer).
   */
  private estimatePlaybackTailMs(): number {
    const diag = this.playback?.getPlaybackDiagnostics?.();
    if (!diag) return 200;
    const SOURCE_SR = 24000;
    const queuedMs = diag.bufferedSamples > 0
      ? (diag.bufferedSamples / SOURCE_SR) * 1000
      : diag.prebufferTimeMs;
    // outputLatency is the browser-stage delay (mixer / sink). Add a small safety
    // tail for the AEC's own decay envelope.
    const ctx = this.audioContext as (AudioContext & { outputLatency?: number }) | null;
    const outputLatencyMs = ctx?.outputLatency ? ctx.outputLatency * 1000 : 0;
    const SAFETY_TAIL_MS = 150;
    return Math.max(150, queuedMs + outputLatencyMs + SAFETY_TAIL_MS);
  }

  finishStreamingResponse(): void {
    this.playback?.finishStreamingResponse();
  }

  /**
   * Check if streaming playback is still active (has scheduled nodes or buffer)
   * This checks if audio is still playing from buffer even if response.done was received
   */
  isStreamingPlaybackActive(): boolean {
    return this.playback?.isStreamingPlaybackActive() ?? false;
  }

  async playAudioStream(stream: MediaStream, id = 'default'): Promise<void> {
    this.playback?.playAudioStream(stream, id);
  }

  pausePlayback(): void {
    this.playback?.pausePlayback();
  }

  resumePlayback(): void {
    this.playback?.resumePlayback();
  }

  stopAllPlayback(): void {
    // Hard stop — speaker is being killed, no tail.
    this.setPlaybackActiveImmediate(false);
    this.playback?.stopAllPlayback();
  }

  isPlaybackActive(): boolean {
    return this.playback?.isPlaybackActive() ?? false;
  }

  getPlaybackFrequencyData(): Uint8Array {
    return this.playback?.getPlaybackFrequencyData() ?? new Uint8Array(9).fill(0);
  }

  /**
   * 🎯 Text-audio sync: Returns accumulated playback time in milliseconds.
   * Only advances while audio is actively playing — freezes during pause.
   */
  getElapsedPlaybackMs(): number {
    return this.playback?.getElapsedPlaybackMs() ?? 0;
  }

  /**
   * 🎯 Text-audio sync: Returns estimated total audio duration in milliseconds
   * for the current response.
   */
  getTotalAudioDurationMs(): number {
    return this.playback?.getTotalAudioDurationMs() ?? 0;
  }

  // ============================================================================
  // Volume Control
  // ============================================================================

  setGain(volume: number): void {
    // Allow volume > 1.0 for amplification (useful for quiet audio sources)
    const clampedVolume = Math.max(0, Math.min(3, volume)); // Max 3x amplification
    this.config.volume = clampedVolume;

    this.recording?.setGain(clampedVolume);
    this.playback?.setVolume(clampedVolume);
  }

  getGain(): number {
    return this.config.volume ?? 1.0;
  }

  // ============================================================================
  // AudioContext Management
  // ============================================================================

  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Diagnostics snapshot for the on-screen audio diagnostics overlay.
   * Returns null if audio is not initialized.
   */
  getDiagnostics() {
    if (!this.audioContext) return null;
    const ctx = this.audioContext as AudioContext & { outputLatency?: number };
    return {
      contextState: this.audioContext.state,
      contextCurrentTime: this.audioContext.currentTime,
      contextSampleRate: this.audioContext.sampleRate,
      baseLatencyMs: (this.audioContext.baseLatency ?? 0) * 1000,
      outputLatencyMs: (ctx.outputLatency ?? 0) * 1000,
      microphoneReady: this.microphoneReady,
      recording: this.recording?.getRecordingDiagnostics() ?? null,
      playback: this.playback?.getPlaybackDiagnostics() ?? null,
    };
  }

  async resumeAudioContext(): Promise<void> {
    if (!this.audioContext) {
      console.warn('[🔴 CHROME] ⚠️ resumeAudioContext called but AudioContext not initialized');
      return;
    }

    if (this.audioContext.state === 'suspended') {
      logger.debug('[🔴 CHROME] 🎵 Resuming AudioContext...');
      try {
        await this.audioContext.resume();
        logger.debug('[🔴 CHROME] ✅ AudioContext resumed! State:', this.audioContext.state);
        
        // Reconfigure audioSession after resume
        this.configureAudioSession();
      } catch (error) {
        console.error('[🔴 CHROME] ❌ Failed to resume AudioContext:', error);
      }
    } else {
      // Even if already resumed, ensure audioSession is correct
      this.configureAudioSession();
    }
  }

  // ============================================================================
  // VAD (Voice Activity Detection)
  // ============================================================================

  getVADState(): VADState {
    return this.recording?.getVADState() ?? {
      gateOpen: false,
      voicedMs: 0,
      silenceMs: 0,
      preRollChunks: 0,
      noiseFloor: 0,
    };
  }

  /**
   * 🛡️ Check if there was recent audio activity (for speech_stopped validation)
   */
  hasRecentAudioActivity(lookbackMs: number = 500): boolean {
    return this.recording?.hasRecentAudioActivity(lookbackMs) ?? false;
  }

  /**
   * Toggle atypical-voice VAD thresholds (laryngectomy / esophageal speech).
   * Safe to call before initializeAudio() — the value is stored and applied
   * as soon as the recording module is created.
   */
  setAtypicalVoiceMode(enabled: boolean): void {
    this.atypicalVoiceMode = enabled;
    this.recording?.setAtypicalVoiceMode(enabled);
  }

  // ============================================================================
  // Legacy Compatibility (deprecated but kept for backward compatibility)
  // ============================================================================

  async playAudioBuffer(buffer: ArrayBuffer): Promise<void> {
    try {
      if (!this.audioContext) {
        throw new Error('Audio context not initialized');
      }

      const audioBuffer = await this.audioContext.decodeAudioData(buffer);
      const sourceNode = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();

      sourceNode.buffer = audioBuffer;
      gainNode.gain.value = this.config.volume ?? 1.0;

      sourceNode.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      sourceNode.start();

      logger.debug('🔊 Playing audio buffer');

    } catch (error) {
      console.error('Error playing audio buffer:', error);
      this.events.onError?.(error as Error);
    }
  }

  // ============================================================================
  // Cleanup
  // ============================================================================

  async cleanup(): Promise<void> {
    try {
      // Stop all activities
      this.stopRecording();
      this.stopAllPlayback();

      // Cleanup modules
      this.playback?.cleanup();
      this.recording?.cleanup();

      // Clear references
      this.playback = null;
      this.recording = null;
      this.microphoneReady = false;

      // Close audio context
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close();
        this.audioContext = null;
      }

      logger.debug('🧹 Audio cleanup complete');

    } catch (error) {
      console.error('Error during audio cleanup:', error);
      this.events.onError?.(error as Error);
    }
  }
}

export default WebAudioManager;

